import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useAuth } from '@/lib/auth-context'
import { useSetupStatus, useInvalidateSetupStatus } from '@/hooks/use-setup-status'
import { getWizardStep, type HardSetupItem } from '@/lib/setup-status'
import { generateIdempotencyKey } from '@/lib/idempotency'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Egg, LogOut } from 'lucide-react'
import { maskCnpj, onlyDigits } from '@/lib/br-masks'
import { todayISODate } from '@/lib/br-dates'
import { API_BASE } from '@/lib/api-base'

const STEP_LABELS: Record<HardSetupItem, string> = {
  organization: 'Organização',
  granja: 'Granja',
  admin_user: 'Administrador da granja',
  galpao: 'Galpão',
  lote: 'Lote (plantel)',
}

function SetupPage() {
  const navigate = useNavigate()
  const {
    apiFetch,
    isSuperAdmin,
    organizations,
    tenants,
    selectedOrgId,
    setSelectedOrgId,
    refetchOrganizations,
    refetchTenants,
    setSelectedTenantId,
    switchGranja,
    logout,
    activeGranjaId,
  } = useAuth()
  const { status, isReady, refetch } = useSetupStatus()
  const invalidateSetupStatus = useInvalidateSetupStatus()

  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [createdOrgId, setCreatedOrgId] = useState<string | null>(null)
  const [createdGranjaId, setCreatedGranjaId] = useState<string | null>(null)
  const [createdGalpaoId, setCreatedGalpaoId] = useState<string | null>(null)

  const [orgForm, setOrgForm] = useState({ nome: '', cnpj: '' })
  const [granjaForm, setGranjaForm] = useState({ nome: '', tipo_producao: 'postura' })
  const [userForm, setUserForm] = useState({ nome: '', email: '', senha: '' })
  const [galpaoForm, setGalpaoForm] = useState({ nome: '', capacidade: 5000 })
  const [loteForm, setLoteForm] = useState({
    linhagem: '',
    quantidade_inicial: 1000,
    quantidade_atual: 1000,
    data_alojamento: todayISODate(),
  })

  const currentStep = getWizardStep(status.missing)
  const orgIdFromTenants = tenants.find((t) => t.organization_id)?.organization_id ?? null
  const orgId = createdOrgId ?? selectedOrgId ?? organizations[0]?.id ?? orgIdFromTenants
  const granjaId = createdGranjaId ?? activeGranjaId

  useEffect(() => {
    if (currentStep !== 'lote' || createdGalpaoId) return
    void (async () => {
      const res = await apiFetch(`${API_BASE}/api/galpoes/`)
      if (res.ok) {
        const galpoes = (await res.json()) as { id: string }[]
        if (galpoes.length > 0) setCreatedGalpaoId(galpoes[0].id)
      }
    })()
  }, [currentStep, createdGalpaoId, apiFetch])

  useEffect(() => {
    if (!isReady) return
    if (!status.blocking && !currentStep) {
      void navigate({ to: '/dashboard', replace: true })
    }
  }, [isReady, status.blocking, currentStep, navigate])

  const afterWrite = async () => {
    await invalidateSetupStatus()
    await refetch()
    await refetchTenants()
    if (isSuperAdmin) await refetchOrganizations()
  }

  const activateGranja = async (id: string) => {
    setCreatedGranjaId(id)
    if (isSuperAdmin) {
      setSelectedTenantId(id)
    } else {
      const ok = await switchGranja(id)
      if (!ok) throw new Error('Não foi possível ativar a granja. Tente novamente.')
    }
    await invalidateSetupStatus()
  }

  const handleSelectExistingGranja = async (id: string) => {
    setError(null)
    setLoading(true)
    try {
      await activateGranja(id)
      await afterWrite()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao ativar granja')
    } finally {
      setLoading(false)
    }
  }

  const availableGranjas = isSuperAdmin && selectedOrgId
    ? tenants.filter((t) => t.organization_id === selectedOrgId)
    : tenants

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await apiFetch(`${API_BASE}/api/admin/organizations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: orgForm.nome.trim(),
          cnpj: onlyDigits(orgForm.cnpj) || null,
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Erro ao criar organização')
      }
      const org = (await res.json()) as { id: string }
      setCreatedOrgId(org.id)
      setSelectedOrgId(org.id)
      setOrgForm({ nome: '', cnpj: '' })
      await afterWrite()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar organização')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateGranja = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgId) {
      setError('Selecione ou crie uma organização primeiro.')
      return
    }
    setError(null)
    setLoading(true)
    try {
      const res = await apiFetch(`${API_BASE}/api/organizations/${orgId}/granjas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: granjaForm.nome.trim(),
          tipo_producao: granjaForm.tipo_producao || 'postura',
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Erro ao criar granja')
      }
      const granja = (await res.json()) as { id: string }
      setGranjaForm({ nome: '', tipo_producao: 'postura' })
      await activateGranja(granja.id)
      await afterWrite()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar granja')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateAdminUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!granjaId) {
      setError('Ative uma granja antes de criar o administrador.')
      return
    }
    setError(null)
    setLoading(true)
    try {
      const res = await apiFetch(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: userForm.nome.trim(),
          email: userForm.email.trim(),
          senha: userForm.senha,
          role: 'user',
          granja_id: granjaId,
          role_granja: 'admin',
          role_org: 'admin_org',
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Erro ao criar administrador')
      }
      setUserForm({ nome: '', email: '', senha: '' })
      await afterWrite()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar administrador')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateGalpao = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!granjaId) {
      setError('Selecione uma granja ativa antes de criar o galpão.')
      return
    }
    setError(null)
    setLoading(true)
    try {
      const res = await apiFetch(`${API_BASE}/api/galpoes/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Idempotency-Key': generateIdempotencyKey(),
        },
        body: JSON.stringify({
          nome: galpaoForm.nome.trim(),
          capacidade: galpaoForm.capacidade,
          status: 'ativo',
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Erro ao criar galpão')
      }
      const galpao = (await res.json()) as { id: string }
      setCreatedGalpaoId(galpao.id)
      setGalpaoForm({ nome: '', capacidade: 5000 })
      await afterWrite()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar galpão')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateLote = async (e: React.FormEvent) => {
    e.preventDefault()
    const galpaoId = createdGalpaoId
    if (!galpaoId) {
      setError('Crie um galpão antes de cadastrar o lote.')
      return
    }
    setError(null)
    setLoading(true)
    try {
      const res = await apiFetch(`${API_BASE}/api/lotes/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          galpao_id: galpaoId,
          linhagem: loteForm.linhagem.trim(),
          quantidade_inicial: loteForm.quantidade_inicial,
          data_alojamento: loteForm.data_alojamento,
          status: 'ativo',
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Erro ao criar lote')
      }
      setLoteForm({
        linhagem: '',
        quantidade_inicial: 1000,
        quantidade_atual: 1000,
        data_alojamento: todayISODate(),
      })
      await afterWrite()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar lote')
    } finally {
      setLoading(false)
    }
  }

  if (!isReady || (!status.blocking && !currentStep)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-muted-foreground">Carregando configuração...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b bg-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 font-semibold">
          <Egg className="h-6 w-6 text-primary" />
          <span>GranjaSmart — Configuração inicial</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void logout()}>
          <LogOut className="h-4 w-4 mr-2" />
          Sair
        </Button>
      </header>

      <main className="max-w-lg mx-auto py-10 px-4">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Configure sua granja</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Complete os passos abaixo para liberar o sistema operacional.
          </p>
        </div>

        {currentStep && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{STEP_LABELS[currentStep]}</CardTitle>
            </CardHeader>
            <CardContent>
              {error && <p className="text-destructive text-sm mb-4">{error}</p>}

              {currentStep === 'organization' && (
                <form onSubmit={handleCreateOrg} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="org-nome">Nome da organização</Label>
                    <Input
                      id="org-nome"
                      value={orgForm.nome}
                      onChange={(e) => setOrgForm({ ...orgForm, nome: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="org-cnpj">CNPJ (opcional)</Label>
                    <Input
                      id="org-cnpj"
                      value={orgForm.cnpj}
                      onChange={(e) => setOrgForm({ ...orgForm, cnpj: maskCnpj(e.target.value) })}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Salvando...' : 'Criar organização'}
                  </Button>
                </form>
              )}

              {currentStep === 'granja' && (
                <div className="space-y-6">
                  {availableGranjas.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        Selecione uma granja existente para continuar a configuração:
                      </p>
                      {availableGranjas.map((g) => (
                        <Button
                          key={g.id}
                          type="button"
                          variant="outline"
                          className="w-full justify-start"
                          disabled={loading}
                          onClick={() => void handleSelectExistingGranja(g.id)}
                        >
                          {g.nome}
                        </Button>
                      ))}
                      <p className="text-xs text-muted-foreground text-center">ou crie uma nova</p>
                    </div>
                  )}
                  <form onSubmit={handleCreateGranja} className="space-y-4">
                  {isSuperAdmin && organizations.length > 0 && !createdOrgId && (
                    <div className="space-y-2">
                      <Label>Organização</Label>
                      <select
                        className="w-full text-sm border rounded-md px-3 py-2 bg-background"
                        value={orgId || ''}
                        onChange={(e) => setSelectedOrgId(e.target.value || null)}
                      >
                        {organizations.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="granja-nome">Nome da granja</Label>
                    <Input
                      id="granja-nome"
                      value={granjaForm.nome}
                      onChange={(e) => setGranjaForm({ ...granjaForm, nome: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="granja-tipo">Tipo de produção</Label>
                    <select
                      id="granja-tipo"
                      className="w-full text-sm border rounded-md px-3 py-2 bg-background"
                      value={granjaForm.tipo_producao}
                      onChange={(e) => setGranjaForm({ ...granjaForm, tipo_producao: e.target.value })}
                    >
                      <option value="postura">Postura</option>
                      <option value="corte">Corte</option>
                    </select>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Salvando...' : 'Criar granja'}
                  </Button>
                </form>
                </div>
              )}

              {currentStep === 'admin_user' && (
                <form onSubmit={handleCreateAdminUser} className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Crie o usuário administrador que operará esta granja no dia a dia.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="user-nome">Nome completo</Label>
                    <Input
                      id="user-nome"
                      value={userForm.nome}
                      onChange={(e) => setUserForm({ ...userForm, nome: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-email">E-mail</Label>
                    <Input
                      id="user-email"
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-senha">Senha inicial</Label>
                    <Input
                      id="user-senha"
                      type="password"
                      value={userForm.senha}
                      onChange={(e) => setUserForm({ ...userForm, senha: e.target.value })}
                      required
                      minLength={8}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Salvando...' : 'Criar administrador'}
                  </Button>
                </form>
              )}

              {currentStep === 'galpao' && (
                <form onSubmit={handleCreateGalpao} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="galpao-nome">Nome do galpão</Label>
                    <Input
                      id="galpao-nome"
                      value={galpaoForm.nome}
                      onChange={(e) => setGalpaoForm({ ...galpaoForm, nome: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="galpao-cap">Capacidade (aves)</Label>
                    <Input
                      id="galpao-cap"
                      type="number"
                      min={1}
                      value={galpaoForm.capacidade}
                      onChange={(e) =>
                        setGalpaoForm({ ...galpaoForm, capacidade: Number(e.target.value) })
                      }
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Salvando...' : 'Criar galpão'}
                  </Button>
                </form>
              )}

              {currentStep === 'lote' && (
                <form onSubmit={handleCreateLote} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="lote-linhagem">Linhagem</Label>
                    <Input
                      id="lote-linhagem"
                      value={loteForm.linhagem}
                      onChange={(e) => setLoteForm({ ...loteForm, linhagem: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lote-qtd">Quantidade inicial</Label>
                    <Input
                      id="lote-qtd"
                      type="number"
                      min={1}
                      value={loteForm.quantidade_inicial}
                      onChange={(e) => {
                        const q = Number(e.target.value)
                        setLoteForm({
                          ...loteForm,
                          quantidade_inicial: q,
                          quantidade_atual: q,
                        })
                      }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lote-data">Data de alojamento</Label>
                    <Input
                      id="lote-data"
                      type="date"
                      value={loteForm.data_alojamento}
                      onChange={(e) => setLoteForm({ ...loteForm, data_alojamento: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Salvando...' : 'Criar lote'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {status.missing.length > 0 && (
          <p className="text-xs text-muted-foreground mt-4 text-center">
            Pendente: {status.missing.join(', ')}
          </p>
        )}
      </main>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/setup')({
  component: SetupPage,
})
